import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Search, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// iter298 — Shared, canonical membership plan picker. Reads configured plans from
// GET /api/membership-plans (db.system_settings) — NOT hardcoded. Reusable from both
// the CompletionWizard and manual invoices. On select it returns a STRUCTURED
// membership line (plan identity + configured price + cadence), never free text.
export const MembershipPlanPicker = ({ open, onOpenChange, onSelect, currentMembership }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [cadenceByPlan, setCadenceByPlan] = useState({});
  const [complimentary, setComplimentary] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/membership-plans`)
      .then(({ data }) => setPlans(data?.plans || []))
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
  }, [open]);

  const money = (n) => {
    const v = Number(n || 0);
    return `$${Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const filtered = plans.filter((p) => {
    const t = `${p.display_name || p.label} ${p.subtitle || ''} ${p.badge || ''}`.toLowerCase();
    return t.includes(q.toLowerCase().trim());
  });

  const choose = (plan) => {
    const cadence = plan.allow_monthly ? (cadenceByPlan[plan.key] || 'annual') : 'annual';
    const price = cadence === 'monthly' ? Number(plan.monthly_price || 0) : Number(plan.annual_price || 0);
    const cadenceLabel = cadence === 'monthly' ? '/mo' : '/yr';
    onSelect({
      description: `${plan.display_name || plan.label} Membership${complimentary ? ' (Complimentary)' : ` (${money(price)}${cadenceLabel})`}`,
      quantity: 1,
      unit_price: complimentary ? 0 : price,
      kind: 'membership',
      membership_plan_key: plan.key,
      plan_name: plan.display_name || plan.label,
      billing_cadence: cadence,
      complimentary,
    });
    onOpenChange(false);
    setQ(''); setComplimentary(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-lg" data-testid="membership-plan-picker">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">Select Membership Plan</DialogTitle>
        </DialogHeader>

        {currentMembership && (currentMembership.active || currentMembership.status === 'active') && (
          <div data-testid="membership-picker-active-warning" className="flex items-start gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-md p-3">
            <AlertTriangle className="w-4 h-4 text-[#D4AF37] shrink-0 mt-0.5" />
            <p className="text-xs text-[#D4AF37]">
              This customer is already an <b>Active {(currentMembership.tier || '').toUpperCase()}</b> member.
              Selling another plan may create a duplicate — use a renewal/upgrade instead unless this is intentional.
            </p>
          </div>
        )}
        {currentMembership && currentMembership.status === 'pending' && (
          <div data-testid="membership-picker-pending-warning" className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/40 rounded-md p-3">
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-300">
              A membership sale is already <b>Pending payment</b> for this customer. Complete that payment instead of adding another.
            </p>
          </div>
        )}

        {plans.length > 4 && (
          <div className="relative">
            <Search className="w-4 h-4 text-[#525252] absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="membership-picker-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search plans…"
              className="w-full bg-[#0A0A0A] border border-[#262626] rounded-md pl-9 pr-3 h-10 text-sm text-white" />
          </div>
        )}

        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {loading && <div className="flex items-center gap-2 text-[#A3A3A3] text-sm p-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading plans…</div>}
          {!loading && filtered.length === 0 && <p className="text-[#525252] text-sm p-4">No active membership plans configured.</p>}
          {!loading && filtered.map((plan) => {
            const cadence = plan.allow_monthly ? (cadenceByPlan[plan.key] || 'annual') : 'annual';
            const price = cadence === 'monthly' ? Number(plan.monthly_price || 0) : Number(plan.annual_price || 0);
            return (
              <div key={plan.key} data-testid={`membership-plan-${plan.key}`}
                className="border border-[#262626] rounded-md p-3 hover:border-[#D4AF37]/60 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium">{plan.display_name || plan.label}</p>
                      {plan.badge && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#D4AF37]/15 text-[#D4AF37]">{plan.badge}</span>}
                    </div>
                    {plan.subtitle && <p className="text-[#A3A3A3] text-xs mt-0.5">{plan.subtitle}</p>}
                    <p className="text-[#D4AF37] text-sm mt-1" data-testid={`membership-plan-price-${plan.key}`}>
                      {complimentary ? 'Complimentary ($0)' : `${money(price)} ${cadence === 'monthly' ? '/ month' : '/ year'}`}
                    </p>
                    {plan.allow_monthly && !complimentary && (
                      <div className="flex gap-1 mt-1.5">
                        {['annual', 'monthly'].map((c) => (
                          <button key={c} type="button"
                            onClick={() => setCadenceByPlan((s) => ({ ...s, [plan.key]: c }))}
                            data-testid={`membership-cadence-${plan.key}-${c}`}
                            className={`text-[10px] px-2 py-0.5 rounded border ${cadence === c ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-[#333] text-[#737373]'}`}>
                            {c === 'annual' ? `Annual ${money(plan.annual_price)}` : `Monthly ${money(plan.monthly_price)}`}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button type="button" onClick={() => choose(plan)} data-testid={`membership-plan-select-${plan.key}`}
                    className="shrink-0 bg-[#D4AF37] text-black font-bold text-xs px-3 h-9 rounded-md hover:bg-[#c19f30] inline-flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" /> Select
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <label className="flex items-center gap-2 text-xs text-[#A3A3A3] cursor-pointer pt-1" data-testid="membership-picker-complimentary">
          <input type="checkbox" checked={complimentary} onChange={(e) => setComplimentary(e.target.checked)} className="accent-[#D4AF37]" />
          Complimentary / no charge (activates immediately, $0)
        </label>
      </DialogContent>
    </Dialog>
  );
};

export default MembershipPlanPicker;
